import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'
import FormError from '@components/FormError'
import FormLoader from '@components/FormLoader'
import moment from 'moment'
import DatePicker from '@components/Form/DatePicker'
import Button from '@components/Button'
import Spinner from '@components/spinner'
import { stringifyDate } from '@atheneum/api/dist/lib'
import { capi, configurationApi } from '@atheneum/api'

class SetInvoiceCreatedStatusModal extends Component {
  constructor(props) {
    super(props)

    this.state = {
      error: null,
      loading: true,
      bookingStopDate: null
    }

    this.getBookingStopDate()
  }

  getBookingStopDate = async () => {
    try {
      const bookingStopDate = await configurationApi.getByKey('BOOKING_STOP_DATE')
      this.setState({
        bookingStopDate: bookingStopDate.value,
        error: null,
        loading: false
      })
    } catch (err) {
      this.setState({
        error: err,
        loading: false
      })
    }
  }

  handleSetSentDate = (form, sentDate) => {
    form.setFieldValue('sentDate', sentDate)
    form.setFieldTouched('sentDate', true, true)

    if (!this.props.invoice.isCreditNote) {
      // due date should automatically be set to 14 days after sent date but still be editable
      const dueDate = moment(sentDate)
        .add(14, 'days')
        .toDate()
      form.setFieldValue('dueDate', dueDate)
      form.setFieldTouched('dueDate', true, true)
    }
  }

  handleExit = () => {
    this.props.modalStore.close()
  }

  handleSubmit = async (values, actions) => {
    const { invoiceStore, modalStore, growlStore, invoice } = this.props

    this.setState({ error: null })
    modalStore.close()
    try {
      const data = {
        sentDate: stringifyDate(values.sentDate),
        dueDate: stringifyDate(values.dueDate),
        documentDate: stringifyDate(values.documentDate),
        paidDate: values.paidDate ? stringifyDate(values.paidDate) : undefined
      }

      growlStore.showSuccess('Updating invoice status ...')
      if (invoice) {
        growlStore.showSuccess('Checking invoice id ...')
        const capiInvoice = await capi.checkInvoice({
          invoiceId: invoice.id
        })

        if (capiInvoice) {
          await capi.updateInvoiceStatus({
            invoiceId: invoice.id,
            status: 'created'
          })
        }
      }

      await invoiceStore.updateToCreatedStatus(invoice, data)
      invoiceStore.get(invoice.id)
    } catch (error) {
      actions.setSubmitting(false)
      this.setState({ error })
      growlStore.showError(error)
    }
  }

  renderTitle = () => {
    const { invoice } = this.props
    if (invoice.isCreditNote) {
      return (
        <h1 className='left-text'>
          Update credit note {invoice.name} to Approved
        </h1>
      )
    } else {
      return (
        <h1 className='left-text'>Update invoice {invoice.name} to Created</h1>
      )
    }
  }

  render() {
    const { invoice } = this.props.invoiceStore
    const today = new Date()

    if (this.state.loading) return <Spinner />

    return (
      <Formik
        initialValues={initializeForm(invoice.isCreditNote)}
        validationSchema={getValidationSchema(invoice.isCreditNote)}
        onSubmit={this.handleSubmit}
        render={form => (
          <Form onSubmit={form.handleSubmit}>
            <FormLoader loading={form.isSubmitting} />
            {this.renderTitle()}
            <FormError error={this.state.error} />

            <div className='separator hr-separator' />

            <div className='modalWrapper__form'>
              <Field
                name='sentDate'
                label='Send date'
                maxDate={invoice.isCreditNote ? today : null}
                onChange={date => this.handleSetSentDate(form, date)}
                component={DatePicker}
              />

              <Field
                name='dueDate'
                label='Due date'
                maxDate={invoice.isCreditNote ? today : null}
                component={DatePicker}
              />

              <Field
                name='documentDate'
                label='Document date'
                minDate={this.state.bookingStopDate}
                maxDate={today}
                component={DatePicker}
              />

              {invoice.isCreditNote && (
                <Field
                  name='paidDate'
                  label='Paid date'
                  maxDate={today}
                  component={DatePicker}
                />
              )}
            </div>

            <div className='separator hr-separator' />

            <div className='modalWrapper__buttonWrapper'>
              <Button disabled={form.isSubmitting} submit primary>
                {invoice.isCreditNote
                  ? 'Create Credit note'
                  : 'Set Created status'}
              </Button>
              <Button
                disabled={form.isSubmitting}
                secondary
                onClick={this.handleExit}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      />
    )
  }
}

SetInvoiceCreatedStatusModal.propTypes = {
  modalStore: PropTypes.object,
  invoiceStore: PropTypes.object,
  growlStore: PropTypes.object,
  invoice: PropTypes.object
}

SetInvoiceCreatedStatusModal.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  class: 'modal__content--small'
}

function initializeForm(isCreditNote) {
  if (isCreditNote) {
    return {
      sentDate: new Date(),
      dueDate: new Date(),
      documentDate: new Date(),
      paidDate: new Date(),
      comment: ''
    }
  } else {
    return {
      sentDate: null,
      dueDate: null,
      documentDate: null
    }
  }
}

function getValidationSchema(isCreditNote) {
  let validationSchemaScheme = {
    sentDate: yup
      .string()
      .required('Sent date field is required')
      .typeError('Sent date field is required'),
    dueDate: yup
      .string()
      .required('Due date field is required')
      .typeError('Due date field is required'),
    documentDate: yup
      .string()
      .required('Document date field is required')
      .typeError('Document date field is required')
  }

  if (isCreditNote) {
    validationSchemaScheme.paidDate = yup
      .string()
      .required('Document date field is required')
      .typeError('Document date field is required')
  }

  return yup.object().shape(validationSchemaScheme)
}

export default inject(
  'invoiceStore',
  'modalStore',
  'growlStore'
)(observer(SetInvoiceCreatedStatusModal))
